import React, { useState } from 'react';
import { graphql } from 'gatsby';
import { GatsbySanityImageFluidNoBase64, RawPortableText } from '../../types';
import Img from 'gatsby-image/withIEPolyfill';
import clsx from 'clsx';
import BlockContent from '@sanity/block-content-to-react';
import backgroundWatermark from '../../assets/images/graphic_elements/circles_tabbedSection.svg';
import Accordion from '../Accordion';
import serializers from '../../serializers';
import { CommonSectionProps, SectionBgColor } from '../../templates/Page';
import { areSameFlatColor } from '../../utils/utils';

import * as styles from './TabbedListSection.module.scss';

export const TabbedListSectionFragment = graphql`
  fragment TabbedListSection on SanityTabbedListSection {
    title
    tabs {
      title
      _rawText(resolveReferences: { maxDepth: 4 })
      images {
        asset {
          fluid(maxWidth: 760, maxHeight: 350) {
            ...GatsbySanityImageFluid_noBase64
          }
        }
      }
    }
    withBackgroundWatermark
  }
`;

export type TabbedListSectionProps = {
  title: string;
  tabs: Array<{
    title: string;
    _rawText: RawPortableText;
    images: Array<{
      asset: {
        fluid: GatsbySanityImageFluidNoBase64;
      };
    }>;
  }>;
  withBackgroundWatermark?: boolean;
  className?: string;
};

export function getSectionBgColor(props: TabbedListSectionProps): SectionBgColor<'white'> {
  return {
    name: 'white',
    type: 'solid',
  };
}

function TabbedListSection<PrevSectionColorName extends string>(
  props: TabbedListSectionProps & CommonSectionProps<PrevSectionColorName>,
): React.ReactElement {
  const { title, tabs, withBackgroundWatermark, className, previousSectionBgColor } = props;

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  return (
    <section
      id={props.sectionID || ''}
      className={clsx(
        styles.sectionContainer,
        className,
        areSameFlatColor(getSectionBgColor(props), previousSectionBgColor) && styles.noPaddingTop,
      )}
    >
      <div className={clsx(styles.section, withBackgroundWatermark && styles.withMinHeight)}>
        {title && <h2 className={styles.title}>{title}</h2>}
        <div className={styles.tabbedListContainerDesktop}>
          {withBackgroundWatermark && (
            <div className={styles.backgroundWatermarkContainer}>
              <img src={backgroundWatermark} alt="" />
            </div>
          )}
          <div className={styles.tabsTitleContainer}>
            {tabs.map((tab, i) => (
              <nav
                className={clsx(styles.tabTitle, activeTabIndex === i && styles.active)}
                key={i}
                onClick={() => setActiveTabIndex(i)}
              >
                {tab.title}
              </nav>
            ))}
          </div>
          {tabs.map((tab, i) => (
            <div
              className={clsx(styles.tabsTextContainer, activeTabIndex === i && styles.active)}
              key={i}
            >
              <h3>{tab.title}</h3>
              <BlockContent
                className={styles.text}
                blocks={tab._rawText}
                serializers={serializers}
              />
              <div className={styles.tabImagesContainer}>
                {tab.images.map((image, i) => (
                  <Img
                    key={i}
                    className={styles.image}
                    loading="eager"
                    fadeIn={false}
                    fluid={image.asset.fluid}
                    alt=""
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.tabbedListContainerMobile}>
          {tabs.map((tab, i) => (
            <Accordion startsOpen={i === 0 && true} key={i} title={tab.title} useGreenAccordion>
              <BlockContent
                className={styles.text}
                blocks={tab._rawText}
                serializers={serializers}
              />
              <div className={styles.tabImagesContainer}>
                {tab.images.map((image, i) => (
                  <Img
                    key={i}
                    className={styles.image}
                    loading="eager"
                    fadeIn={false}
                    fluid={image.asset.fluid}
                    alt=""
                  />
                ))}
              </div>
            </Accordion>
          ))}
        </div>
      </div>
    </section>
  );
}

export default TabbedListSection;
